import React from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Col, ListGroup, Row } from 'reactstrap';
import OperationInProgressPrompt from '../../../components/OperationInProgressPrompt';
import { Me, ReduxState } from '../../../types';
import PersonalObjectiveItemRoot from '../../Person/PersonalObjectiveItemRoot';
import EmptyState from '../../Widgets/EmptyState';
import Loading from '../../Widgets/Loading';
import ObjectivesTimeFrameSelector from '../ObjectivesTimeFrameSelector';
import {
  NO_OBJECTIVE_TYPES,
  useObjectiveTypes,
} from '../ObjectiveTypeOperations';
import { useAvailableScopes, useObjectivesOverview } from './hooks';
import ObjectivesOverviewItemHeader from './ObjectivesOverviewItemHeader';
import ObjectivesOverviewScopeFilter from './ObjectivesOverviewScopeFilter';

interface Props {
  me: Me;
}

const ObjectivesOverviewPage: React.FC<Props> = ({ me }) => {
  const { formatMessage } = useIntl();
  const { organizationObjectiveTypes } = useObjectiveTypes();

  const availableScopes = useAvailableScopes();

  const {
    handleViewNextTimeframe,
    currentTimeframeText,
    handleViewPreviousTimeframe,
    isLoadingMoreItems,
    handleFilterChange,
    viewStatus,
    viewScope,
    viewPeopleFilters,
    allObjectives,
    loadMoreRef,
    handleGenerateCSV,
    handleGenerateCSVCompleted,
    status,
    firstDay,
    lastDay,
    viewTextFilter,
    viewTagNames,
    viewObjectiveTypeIds,
    isTaskInProgress,
  } = useObjectivesOverview({ availableScopes });

  if (status === 'ERROR' && !isLoadingMoreItems) {
    return <EmptyState title={'Error.... OOPS'} />;
  }

  return (
    <>
      <OperationInProgressPrompt
        hasPendingChanges={isTaskInProgress}
        message={formatMessage({
          id: 'app.views.objectives.objectives_overview_page.download_in_progress',
          defaultMessage:
            'A download is in progress. Are you sure you want to leave this page?',
        })}
      />
      <Row>
        <Col>
          <ObjectivesTimeFrameSelector
            viewNextTimeframe={handleViewNextTimeframe}
            currentTimeframeText={currentTimeframeText}
            viewPreviousTimeframe={handleViewPreviousTimeframe}
            disabled={
              (status === 'LOADING' || status == 'DISABLED') &&
              !isLoadingMoreItems
            }
          />
          <ObjectivesOverviewScopeFilter
            onChange={handleFilterChange}
            onGenerateCSV={handleGenerateCSV}
            onGenerateCSVCompleted={handleGenerateCSVCompleted}
            status={viewStatus}
            objectiveTypeIds={viewObjectiveTypeIds}
            organizationObjectiveTypes={organizationObjectiveTypes}
            scope={viewScope}
            peopleFilters={viewPeopleFilters}
            availableScopes={availableScopes}
            textFilter={viewTextFilter}
            tagNames={viewTagNames}
            firstDay={firstDay}
            lastDay={lastDay}
            disabled={
              (status === 'LOADING' || status == 'DISABLED') &&
              !isLoadingMoreItems
            }
            hasData={allObjectives.length > 0}
          />
          {status === 'LOADING' && !isLoadingMoreItems && <Loading />}
          {status === 'SUCCESS' && allObjectives.length === 0 && (
            <EmptyState title={'No Data'} />
          )}
          {(status === 'SUCCESS' ||
            status === 'DISABLED' ||
            isLoadingMoreItems) &&
            allObjectives.length > 0 && (
              <ListGroup>
                <ObjectivesOverviewItemHeader />
                {allObjectives.map((objective, index) => (
                  <PersonalObjectiveItemRoot
                    isEditable={false}
                    isInPopover={false}
                    hideScoresAndWeight={false}
                    hideRelatedObjectives={false}
                    person={me}
                    key={objective.key}
                    // @ts-expect-error
                    objective={objective}
                    showLabelHeadings={index === 0}
                    forceShowChildren={false}
                    enableParentEdit={false}
                    showDescendantsCount={true}
                    showDescendantsBreakdown={true}
                    textToHighlight={viewTextFilter}
                    applyFiltering={true}
                    organizationObjectiveTypes={organizationObjectiveTypes}
                    userEditableObjectiveTypes={NO_OBJECTIVE_TYPES}
                  />
                ))}
                <div ref={loadMoreRef} />
                {isLoadingMoreItems && <Loading />}
              </ListGroup>
            )}
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = (state: ReduxState) => {
  const { me } = state;
  return {
    me,
  };
};

const connector = connect(mapStateToProps);

export default connector(React.memo(ObjectivesOverviewPage));
